import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import PrerequisiteGate from '../components/PrerequisiteGate';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Camera, Loader2, MapPin, Shirt, Sun, Lock, RefreshCw, Sliders, MessageCircle } from 'lucide-react';
import AIRefinementPanel from '../components/AIRefinementPanel';
import { useTranslation } from 'react-i18next';

const PhotoshootConceptContent = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [concept, setConcept] = useState(null);
  const [showRefinementPanel, setShowRefinementPanel] = useState(false);
  const [formData, setFormData] = useState({
    brand_vibe: '',
    season: ''
  });

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  const hasAccess = !SHOW_PRICING || tier === 'premium' || tier === 'pro' || tier === 'experience' || isBetaUser;

  if (!hasAccess) {
    return (
      <DashboardLayout title="Photoshoot Concept Generator">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <Lock className="text-[#D4AF37] mx-auto mb-6" size={64} />
            <h2 className="font-serif text-2xl text-white mb-4">Premium Feature</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Photoshoot Concept Generator is available for Premium and Pro subscribers.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="btn-gold px-8 py-4" data-testid="upgrade-btn">
              Upgrade to Premium
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  const handleGenerate = async () => {
    if (!formData.brand_vibe) {
      toast.error('Please enter your brand vibe');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/photoshoot/generate`, formData, { headers });
      setConcept(response.data);
      toast.success('Photoshoot concept generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate concept');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Photoshoot Concept Generator">
      <div className="max-w-6xl mx-auto">
        {/* Input */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-8 mb-8">
          <div className="mb-6">
            <Camera className="text-[#E5C585] mb-4" size={28} />
            <h2 className="font-serif text-2xl text-white mb-2">Create Photoshoot Concept</h2>
            <p className="text-[#A1A1AA]">Get creative direction for your brand photoshoots.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-[#A1A1AA] text-sm mb-2 block">Brand Vibe</Label>
              <Input
                value={formData.brand_vibe}
                onChange={(e) => setFormData({ ...formData, brand_vibe: e.target.value })}
                placeholder="e.g., Elegant, Bold, Minimal"
                className="input-underline"
                data-testid="brand-vibe-input"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-sm mb-2 block">Season (Optional)</Label>
              <Select value={formData.season} onValueChange={(value) => setFormData({ ...formData, season: value })}>
                <SelectTrigger className="bg-transparent border-b border-[#27272A] rounded-none text-white" data-testid="season-select">
                  <SelectValue placeholder="Select season" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
                  <SelectItem value="spring">Spring</SelectItem>
                  <SelectItem value="summer">Summer</SelectItem>
                  <SelectItem value="fall">Fall</SelectItem>
                  <SelectItem value="winter">Winter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={handleGenerate} disabled={loading} className="btn-gold px-8 py-4 mt-6 flex items-center gap-2" data-testid="generate-concept-btn">
            {loading ? (<><Loader2 className="animate-spin" size={18} />Generating...</>) : (<><Camera size={18} />Generate Concept</>)}
          </Button>
        </motion.div>

        {/* Concept */}
        {concept && (
          <div className="space-y-6">
            {/* Interactive AI Buttons */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2">
              <Button variant="outline" onClick={handleGenerate} disabled={loading} className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3">
                <RefreshCw size={16} className="mr-2" />Regenerate
              </Button>
              <Button variant="outline" onClick={() => setShowRefinementPanel(true)} className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3">
                <Sliders size={16} className="mr-2" />Adjust
              </Button>
              <Button variant="outline" onClick={() => setShowRefinementPanel(true)} className="flex-1 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 py-3">
                <MessageCircle size={16} className="mr-2" />Ask AI
              </Button>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-8">
              <span className="text-[#E5C585] text-xs font-accent tracking-widest">CONCEPT</span>
              <h3 className="font-serif text-3xl text-white mt-2 mb-4">{concept.concept_name}</h3>
              <p className="text-[#A1A1AA] text-lg leading-relaxed">{concept.mood}</p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="card-premium p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="text-[#D4AF37]" size={20} />
                  <h4 className="font-serif text-lg text-white">Locations</h4>
                </div>
                <ul className="space-y-3">
                  {concept.locations?.map((loc, i) => (<li key={i} className="text-[#A1A1AA] flex items-start gap-3"><span className="text-[#D4AF37] mt-1">•</span>{loc}</li>))}
                </ul>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-premium p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Shirt className="text-[#E5C585]" size={20} />
                  <h4 className="font-serif text-lg text-white">Outfit Ideas</h4>
                </div>
                <ul className="space-y-3">
                  {concept.outfit_ideas?.map((outfit, i) => (<li key={i} className="text-[#A1A1AA] flex items-start gap-3"><span className="text-[#D4AF37] mt-1">•</span>{outfit}</li>))}
                </ul>
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="card-premium p-6">
              <div className="flex items-center gap-3 mb-4">
                <Sun className="text-[#D4AF37]" size={20} />
                <h4 className="font-serif text-lg text-white">Visual Atmosphere</h4>
              </div>
              <p className="text-[#A1A1AA] leading-relaxed">{concept.visual_atmosphere}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-premium p-6">
              <h4 className="font-serif text-lg text-white mb-4">Shot List</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {concept.shot_list?.map((shot, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#1C1C1C] border border-[#27272A]">
                    <span className="text-[#D4AF37] font-serif">{i + 1}</span>
                    <span className="text-[#A1A1AA]">{shot}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="card-premium p-6">
              <h4 className="font-serif text-lg text-white mb-4">Props & Accessories</h4>
              <div className="flex flex-wrap gap-2">
                {concept.props_accessories?.map((prop, i) => (
                  <span key={i} className="px-4 py-2 bg-[#1C1C1C] text-[#A1A1AA] text-sm border border-[#27272A]">{prop}</span>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </div>
      
      {/* AI Refinement Panel */}
      <AIRefinementPanel
        isOpen={showRefinementPanel}
        onClose={() => setShowRefinementPanel(false)}
        toolType="photoshoot_concept"
        currentResult={concept}
        onResultUpdate={(newResult) => {
          setConcept({ ...concept, ...newResult });
          toast.success('Concept updated!');
        }}
        suggestedPrompts={[
          "Make it more luxurious and editorial",
          "Suggest indoor studio alternatives",
          "Give me more casual, lifestyle options",
          "What lighting would work best for this concept?"
        ]}
      />
    </DashboardLayout>
  );
};

// Wrap with PrerequisiteGate
const PhotoshootConcept = () => {
  return (
    <PrerequisiteGate 
      toolId="photoshoot-concept" 
      toolTitle="Concepts Photo"
    >
      <PhotoshootConceptContent />
    </PrerequisiteGate>
  );
};

export default PhotoshootConcept;
